import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { BarChart3, Plus, Sparkles, FileEdit } from 'lucide-react';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import CreatePollDialog from '@/components/CreatePollDialog';
import PollCard from '@/components/PollCard';

/**
 * Community Polls feed for the current user — shows active polls from their
 * groups, connections, and city. Polls gauge interest BEFORE committing to
 * an event date/location. When a poll is hot, the creator can convert it
 * into a full event with one tap.
 */
const PollsPage = () => {
  const { user } = useAuth();
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  // Saved drafts from the backend. Resume one by passing its id into the dialog.
  const [drafts, setDrafts] = useState([]);
  const [openDraftId, setOpenDraftId] = useState(null);

  const refreshDrafts = async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/poll-drafts`);
      setDrafts(r.data?.drafts || []);
    } catch {
      setDrafts([]);
    }
  };

  const handleResumeDraft = (id) => {
    setOpenDraftId(id);
    setShowCreate(true);
  };

  const handleNewDraft = () => {
    setOpenDraftId(null);
    setShowCreate(true);
  };

  const handleDiscardDraft = async (id) => {
    try {
      await axios.delete(`${API}/poll-drafts/${id}`);
      setDrafts((prev) => prev.filter((d) => d.draft_id !== id));
      toast.success('Draft discarded');
    } catch {
      toast.error('Could not discard draft');
    }
  };

  const fetchPolls = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/polls/feed`);
      setPolls(res.data?.polls || []);
    } catch {
      setPolls([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchPolls();
    refreshDrafts();
  }, [user]);

  const handleCreated = () => {
    fetchPolls();
    refreshDrafts();
  };

  const handleUpdated = (updated) => {
    setPolls((prev) => prev.map((p) => (p.poll_id === updated.poll_id ? updated : p)));
  };

  const handleRemoved = (pollId) => {
    setPolls((prev) => prev.filter((p) => p.poll_id !== pollId));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50/40 to-white">
      <Navbar />

      <main className="container mx-auto max-w-3xl px-4 pt-24 pb-24" data-testid="polls-page">
        {/* Header */}
        <header className="flex items-start justify-between gap-3 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <BarChart3 className="w-7 h-7 text-violet-600" />
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Polls</h1>
            </div>
            <p className="text-sm text-muted-foreground max-w-md">
              Test the waters before booking a venue. Start a poll — if people show
              interest, turn it into an event with one tap.
            </p>
          </div>
          {user && (
            <Button
              onClick={handleNewDraft}
              className="rounded-full bg-violet-600 hover:bg-violet-700 flex-shrink-0"
              data-testid="polls-create-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              New Poll
            </Button>
          )}
        </header>

        {/* Saved drafts — backend-synced across devices. */}
        {user && drafts.length > 0 && (
          <section
            className="mb-6 space-y-2"
            data-testid="my-drafts-section"
          >
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
              <FileEdit className="w-3.5 h-3.5" />
              Your drafts ({drafts.length})
            </p>
            {drafts.map((d) => {
              const p = d.payload || {};
              const updated = (() => {
                try { return new Date(d.updated_at).toLocaleString(undefined, {month:'short', day:'numeric', hour:'numeric', minute:'2-digit'}); }
                catch { return ''; }
              })();
              return (
                <div
                  key={d.draft_id}
                  className="p-4 rounded-2xl border-2 border-amber-200 bg-amber-50 flex items-center gap-3"
                  data-testid={`draft-row-${d.draft_id}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <FileEdit className="w-5 h-5 text-amber-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-sm text-amber-900 truncate">
                      {(p.question || '(no question yet)').slice(0, 80)}
                    </p>
                    <p className="text-xs text-amber-800">
                      Last edited {updated || '—'}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => handleResumeDraft(d.draft_id)}
                    className="rounded-full bg-amber-600 hover:bg-amber-700 h-8 text-xs flex-shrink-0"
                    data-testid={`draft-resume-${d.draft_id}`}
                  >
                    Resume
                  </Button>
                  <button
                    type="button"
                    onClick={() => handleDiscardDraft(d.draft_id)}
                    className="text-xs text-amber-700 hover:text-red-600 underline flex-shrink-0"
                    data-testid={`draft-discard-${d.draft_id}`}
                  >
                    discard
                  </button>
                </div>
              );
            })}
          </section>
        )}

        {!user && (
          <div className="rounded-2xl bg-white border-2 border-violet-100 p-8 text-center">
            <Sparkles className="w-10 h-10 text-violet-500 mx-auto mb-2" />
            <p className="font-semibold mb-1">Sign in to see community polls</p>
            <p className="text-sm text-muted-foreground">
              Polls are private to your groups, connections, and city.
            </p>
          </div>
        )}

        {user && loading && (
          <div className="text-center py-12 text-muted-foreground">Loading polls…</div>
        )}

        {user && !loading && polls.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl bg-white border-2 border-dashed border-violet-200 p-10 text-center"
            data-testid="polls-empty-state"
          >
            <BarChart3 className="w-12 h-12 text-violet-400 mx-auto mb-3" />
            <p className="font-bold text-lg mb-1">No active polls yet</p>
            <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
              Be the first to start one. Polls from your groups, connections, and
              city will show up here.
            </p>
            <Button
              onClick={() => setShowCreate(true)}
              className="rounded-full bg-violet-600 hover:bg-violet-700"
              data-testid="polls-empty-create-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Start a Poll
            </Button>
          </motion.div>
        )}

        {user && !loading && polls.length > 0 && (
          <div className="space-y-4" data-testid="polls-list">
            {polls.map((p) => (
              <PollCard
                key={p.poll_id}
                poll={p}
                onUpdated={handleUpdated}
                onRemoved={handleRemoved}
              />
            ))}
          </div>
        )}
      </main>

      <Footer />

      <CreatePollDialog
        open={showCreate}
        initialDraftId={openDraftId}
        onOpenChange={(o) => {
          setShowCreate(o);
          if (!o) {
            setOpenDraftId(null);
            refreshDrafts();
          }
        }}
        onCreated={handleCreated}
      />
    </div>
  );
};

export default PollsPage;
